import type {ApiRequestOptions, ApiTransport} from '../../types';
import {generateId} from '../../utils/generateId';
import {isPlainObject} from '../../../../utils/isPlainObject';
import type {RpcTransportOptions} from './types';
import {ApiError} from '../errors';

export const jsonRpcTransport =
  (options: RpcTransportOptions = {}): ApiTransport =>
  async (url: string, method: string, params: unknown = {}, requestOptions?: ApiRequestOptions) => {
    const {
      headers: optionHeaders,
      credentials: optionCredentials,
      includeExtraAuthParams = true,
    } = options;

    const mergedHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(optionHeaders ?? {}),
      ...(requestOptions?.headers ?? {}),
    };

    if (includeExtraAuthParams && isPlainObject(params)) {
      // Params are already provided, no need to add extra auth params
    }

    const credentials = requestOptions?.credentials ?? optionCredentials;

    const response = await fetch(url, {
      method: 'POST',
      signal: requestOptions?.signal,
      headers: mergedHeaders,
      credentials,
      body: JSON.stringify({jsonrpc: '2.0', method, params, id: generateId()}),
    });

    const result: unknown = await response.json();

    rpcErrorHandler(response, result);

    if (isPlainObject(result) && 'result' in result) {
      return result.result;
    }
    return result;
  };

const rpcErrorHandler = (response: Response, result: unknown): void => {
  const meta = {status: response.status, statusText: response.statusText};

  if (!response.ok) {
    let message: string | undefined;

    if (typeof result === 'string') {
      message = result.trim() || undefined;
    } else if (isPlainObject(result)) {
      const resultObj = result;
      if (typeof resultObj.message === 'string' && resultObj.message.trim().length > 0) {
        message = resultObj.message.trim();
      } else if (typeof resultObj.error === 'string' && resultObj.error.trim().length > 0) {
        message = resultObj.error.trim();
      } else if (isPlainObject(resultObj.error)) {
        const errorObj = resultObj.error;
        if (typeof errorObj.message === 'string') {
          message = errorObj.message.trim() || undefined;
        }
      }
    }

    const fallback = `HTTP ${String(response.status)}${response.statusText ? ` ${response.statusText}` : ''}`.trim();
    throw new ApiError(message ?? fallback, {
      kind: 'rest',
      status: response.status,
      details: {raw: result, meta},
    });
  }

  if (isPlainObject(result)) {
    const resultObj = result;
    if ('error' in resultObj && resultObj.error) {
      const errObj = resultObj.error;
      let msg = 'RPC error';

      if (isPlainObject(errObj)) {
        const errorObj = errObj;
        if (typeof errorObj.message === 'string' && errorObj.message.trim().length > 0) {
          msg = errorObj.message.trim();
        }
      } else if (typeof errObj === 'string' && errObj.trim().length > 0) {
        msg = errObj.trim();
      }

      let numericCode: number | undefined;
      if (isPlainObject(errObj)) {
        const rawCode = errObj.code;
        numericCode = typeof rawCode === 'number' || typeof rawCode === 'string' ? Number(rawCode) : undefined;
      }

      throw new ApiError(msg, {
        kind: 'rpc',
        status: response.status,
        code: Number.isFinite(numericCode) ? numericCode : undefined,
        details: {raw: result, meta},
      });
    }
  }
};
